import React, { useState, useEffect } from 'react'
import { FormikProps } from 'formik'
import { IntlShape } from 'react-intl'
import { TourDetails, images, TourVideo } from '../../core/_models'
import { ID } from '../../../../../../_dashboard/helpers'
import ImageHandler from '../../components/ImageHandler'
import { FAQ } from './components/FAQ'
import { IncludeExclude } from './components/IncludeExclude'
import { LocationPoint } from './components/LocationPoint'
import { getAll, patchOne, deleteOne } from '../../../../../services/common_requests'
import { useQuery } from 'react-query'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { toast } from 'react-toastify'
import axios from 'axios'
import ImageUploader from '../../../../../../_dashboard/partials/widgets/upload/ImageUploader'
import ImageEditModal from '../../image-edit-modal/ImageEditModal'
import { extractCategoryId, extractCategoryIds } from '../../core/categoryHelpers'
import { useConfirm } from '../../../../../components/ConfirmProvider'
import { convertDurationDisplayValue } from '../../../utils/formatTourDuration'

/** typeof null === 'object' — never read ._id on null */
const toCitySelectValue = (city: unknown): string => {
  if (city == null || city === '') return ''
  if (typeof city === 'object' && city !== null && '_id' in city) {
    const id = (city as {_id?: unknown})._id
    return id != null ? String(id) : ''
  }
  return String(city)
}

interface GeneralTabProps {
  formik: FormikProps<TourDetails>
  selectedLanguage: string
  setSelectedLanguage: (lang: string) => void
  intl: IntlShape
  isNewTour: boolean
}

interface TourCategory {
  _id: string;
  title: string;
  description?: string;
  status?: string;
  translations?: Array<{
    title: string;
    language: string;
  }>;
}

interface TourCity {
  _id: string;
  name: string;
  status?: string;
  country?: string;
  region?: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
  id?: string;
}

interface TourType {
  _id: string;
  title: string;
  description?: string;
  status?: string;
  translations?: Array<{
    title: string;
    language: string;
  }>;
}

const OPTION_PAGE_SIZE = 100
const OPTION_MAX_PAGES = 50

const CARD_BADGE_COLOR_PRESETS = ['#F44D00', '#EB5757', '#9B51E0', '#219653', '#2F80ED', '#333333']

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0

const resolveCategoryTitle = (category: TourCategory, language: string): string => {
  const translatedTitle = category.translations?.find((t) => t.language === language)?.title
  return translatedTitle || category.title || ''
}

const resolveTypeTitle = (type: TourType, language: string): string => {
  const translatedTitle = type.translations?.find((t) => t.language === language)?.title
  return translatedTitle || type.title || ''
}

const fetchAllOptions = async (endpoint: string) => {
  let page = 0
  const allItems = new Map<string, any>()

  while (page < OPTION_MAX_PAGES) {
    const response = await getAll({page, size: OPTION_PAGE_SIZE}, endpoint)
    const items = Array.isArray(response?.data) ? response.data : []

    if (!items.length) break

    const beforeCount = allItems.size
    items.forEach((item: any) => {
      const key = item?._id ? String(item._id) : ''
      if (key) allItems.set(key, item)
    })

    const totalPages = Number(response?.payload?.pagination?.totalPages || 0)
    if (totalPages > 0) {
      if (page + 1 >= totalPages) break
      page += 1
      continue
    }

    if (items.length < OPTION_PAGE_SIZE) break
    if (allItems.size === beforeCount) break
    page += 1
  }

  return Array.from(allItems.values())
}

const GeneralTab: React.FC<GeneralTabProps> = ({
  formik,
  selectedLanguage,
  setSelectedLanguage,
  intl,
  isNewTour
}) => {
  const confirm = useConfirm()
  const [selectedImage, setSelectedImage] = useState<images | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  
  // State for forms visibility
  const [showFAQForm, setShowFAQForm] = useState(false)
  const [showIncludeForm, setShowIncludeForm] = useState(false)
  const [showExcludeForm, setShowExcludeForm] = useState(false)
  const [showEducationForm, setShowEducationForm] = useState(false)
  const [showHealthForm, setShowHealthForm] = useState(false)
  const [showTransportForm, setShowTransportForm] = useState(false)
  
  // State for new form items
  const [newFAQ, setNewFAQ] = useState({ title: '', content: '' })
  const [newInclude, setNewInclude] = useState({ title: '', description: '' })
  const [newExclude, setNewExclude] = useState({ title: '', description: '' })
  const [newEducation, setNewEducation] = useState({ name: '', content: '', distance: '' })
  const [newHealth, setNewHealth] = useState({ name: '', content: '', distance: '' })
  const [newTransport, setNewTransport] = useState({ name: '', content: '', distance: '' })

  // State for category selection
  const [categoryInput, setCategoryInput] = React.useState('')

  // State for types selection
  const [typeInput, setTypeInput] = React.useState('')

  // Fetch all options used in select lists (without backend default page size limits)
  const { data: tourCategoriesResponse, isLoading: categoriesLoading } = useQuery(
    'tour-categories-all',
    () => fetchAllOptions('/tour-categories'),
    {
      refetchOnWindowFocus: false,
      onError: (error) => {
        toast.error('Error fetching tour categories')
      }
    }
  )

  // Fetch tour cities
  const { data: tourCitiesResponse, isLoading: citiesLoading } = useQuery(
    'tour-cities-all',
    () => fetchAllOptions('/tour-cities'),
    {
      refetchOnWindowFocus: false,
      onError: (error) => {
        toast.error('Error fetching tour cities')
      }
    }
  )

  // Fetch tour types
  const { data: tourTypesResponse, isLoading: typesLoading } = useQuery(
    'tour-types-all',
    () => fetchAllOptions('/tour-types'),
    {
      refetchOnWindowFocus: false,
      onError: (error) => {
        toast.error('Error fetching tour types')
      }
    }
  )

  // Extract categories, cities, and types from responses
  const tourCategories = (Array.isArray(tourCategoriesResponse) ? tourCategoriesResponse : []).filter(
    (category: TourCategory) =>
      isNonEmptyString(category?._id) && isNonEmptyString(resolveCategoryTitle(category, selectedLanguage))
  )
  const tourCities = (Array.isArray(tourCitiesResponse) ? tourCitiesResponse : []).filter(
    (city: TourCity) => isNonEmptyString(city?._id) && isNonEmptyString(city?.name)
  )
  const tourTypes = (Array.isArray(tourTypesResponse) ? tourTypesResponse : []).filter(
    (type: TourType) =>
      isNonEmptyString(type?._id) && isNonEmptyString(resolveTypeTitle(type, selectedLanguage))
  )

  const selectedCategories = React.useMemo(() => {
    const categories = Array.isArray(formik.values.category)
      ? formik.values.category
      : formik.values.category
        ? [formik.values.category]
        : []

    return categories.filter((category: any) => {
      const categoryId = extractCategoryId(category)
      if (!isNonEmptyString(categoryId)) return false

      const title =
        category?.translations?.find((t: any) => t.language === selectedLanguage)?.title ||
        category?.title ||
        tourCategories.find((cat: TourCategory) => String(cat._id) === String(categoryId))
          ? resolveCategoryTitle(
              tourCategories.find((cat: TourCategory) => String(cat._id) === String(categoryId)) as TourCategory,
              selectedLanguage,
            )
          : ''

      return isNonEmptyString(title)
    })
  }, [formik.values.category, selectedLanguage, tourCategories])

  const selectedTypes = React.useMemo(() => {
    const types = Array.isArray(formik.values.types)
      ? formik.values.types
      : formik.values.types
        ? [formik.values.types]
        : []

    return types.filter(
      (type: any) =>
        isNonEmptyString(type?._id) &&
        isNonEmptyString(type?.translations?.find((t: any) => t.language === selectedLanguage)?.title || type?.title)
    )
  }, [formik.values.types, selectedLanguage])

  // Get today's date in YYYY-MM-DD format for the min attribute of date inputs
  const today = new Date().toISOString().split('T')[0]

  // Map category IDs to full objects on load or when categories are fetched
  React.useEffect(() => {
    if (!tourCategories.length) return

    const catVal = formik.values.category
    if (!catVal) return

    const categoryIds = extractCategoryIds(catVal)
    if (!categoryIds.length) return

    const rawList = Array.isArray(catVal) ? catVal : [catVal]
    const alreadyMapped =
      rawList.length === categoryIds.length &&
      rawList.every(
        (item: any) =>
          typeof item === 'object' &&
          item != null &&
          isNonEmptyString(item?.translations?.[0]?.title || item?.title),
      )
    if (alreadyMapped) return

    const mapped = categoryIds
      .map((id) => tourCategories.find((cat: any) => String(cat._id) === String(id)))
      .filter(Boolean)
      .map((cat: any) => ({
        _id: cat._id,
        title: cat.title,
        translations: cat.translations || [{ title: cat.title }],
      }))

    if (!mapped.length) return

    const currentIds = extractCategoryIds(formik.values.category).sort().join(',')
    const nextIds = mapped.map((cat) => String(cat._id)).sort().join(',')
    const needsTitleRemap = rawList.some(
      (item: any) =>
        typeof item !== 'object' ||
        item == null ||
        !(item?.translations?.[0]?.title || item?.title),
    )
    if (currentIds !== nextIds || needsTitleRemap) {
      formik.setFieldValue('category', mapped)
    }
  }, [tourCategories, formik.values.category])

  // Map type IDs to full objects (same pattern as categories) so chips stay after save/reload
  React.useEffect(() => {
    if (!tourTypes.length) return

    const typeVal = formik.values.types
    if (!typeVal) return

    const rawList = Array.isArray(typeVal) ? typeVal : [typeVal]
    const typeIds = rawList
      .map((t: any) => (typeof t === 'object' && t != null ? t?._id : t))
      .filter((id: any) => id != null && String(id).trim() !== '')
      .map(String)
    if (!typeIds.length) return

    const alreadyMapped =
      rawList.length === typeIds.length &&
      rawList.every(
        (item: any) =>
          typeof item === 'object' &&
          item != null &&
          isNonEmptyString(item?.translations?.[0]?.title || item?.title),
      )
    if (alreadyMapped) return

    const mapped = typeIds
      .map((id) => tourTypes.find((type: any) => String(type._id) === String(id)))
      .filter(Boolean)
      .map((type: any) => ({
        _id: type._id,
        title: type.title,
        translations: type.translations || [{ title: type.title }],
      }))

    if (!mapped.length) return

    const currentIds = typeIds.sort().join(',')
    const nextIds = mapped.map((t) => String(t._id)).sort().join(',')
    if (currentIds !== nextIds || rawList.some((item: any) => typeof item !== 'object' || !(item?.title || item?.translations?.[0]?.title))) {
      formik.setFieldValue('types', mapped)
    }
  }, [tourTypes, formik.values.types])

  // Remove invalid placeholder/null categories from form state
  React.useEffect(() => {
    const rawCategories = Array.isArray(formik.values.category)
      ? formik.values.category
      : formik.values.category
        ? [formik.values.category]
        : []
    const validCategories = rawCategories.filter((category: any) =>
      isNonEmptyString(extractCategoryId(category)),
    )
    const rawIds = rawCategories.map((category: any) => extractCategoryId(category))
    const validIds = validCategories.map((category: any) => extractCategoryId(category))
    if (JSON.stringify(rawIds) !== JSON.stringify(validIds)) {
      formik.setFieldValue('category', validCategories)
    }
  }, [formik.values.category])

  // Image handling functions
  const handleImageClick = (img: images) => {
    setSelectedImage(img)
    setIsModalOpen(true)
  }
  
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedImage(null)
  }
  
  const handleUpdateImage = async (updatedImage: { url: string; seoUrl?: string }) => {
    if (!selectedImage) return

    try {
      // Create form data with the updated image
      const formData = new FormData()
      if (updatedImage.url !== selectedImage.url) {
        const response = await fetch(updatedImage.url)
        const blob = await response.blob()
        const file = new File([blob], 'updated-image.jpg', { type: blob.type })
        formData.append('image', file)
      }
      if (updatedImage.seoUrl) {
        formData.append('seoUrl', updatedImage.seoUrl)
      }

      // Call patchOne with the updated image
      const response = await patchOne(formData, `/images/${selectedImage._id}`)
      if (response?.payload?.code === 2021) {
        // Update the images in formik state
        if (formik.values.banner?._id === selectedImage._id) {
          formik.setFieldValue('banner', {
            ...selectedImage,
            url: updatedImage.url,
            seoUrl: updatedImage.seoUrl || ''
          })
        } else if (formik.values.gallery?.some(img => img._id === selectedImage._id)) {
          const updatedGalleryImages = formik.values.gallery.map((img: images) =>
            img._id === selectedImage._id
              ? { ...img, url: updatedImage.url, seoUrl: updatedImage.seoUrl || '' }
              : img
          )
          formik.setFieldValue('gallery', updatedGalleryImages)
        }
        handleCloseModal()
        toast.success(
          intl.formatMessage({
            id: 'ADMIN.COMMON.SUCCESS.IMAGE_UPDATED_SUCCESSFULLY',
          }),
          {
            position: 'top-right',
            autoClose: 4000,
          }
        )
      } else {
        toast.error(
          intl.formatMessage({
            id: 'ADMIN.COMMON.ERROR.IMAGE_UPDATED',
          }),
          {
            position: 'top-right',
            autoClose: 4000,
          }
        )
      }
    } catch (error) {
      toast.error(
        intl.formatMessage({
          id: 'ADMIN.COMMON.ERROR.IMAGE_UPDATED',
        }),
        {
          position: 'top-right',
          autoClose: 4000,
        }
      )
    }
  }

  const handleBannerImageAdded = (newImage: { url: string; _id: string }) => {
    // Add the new banner image to the formik values with all necessary fields
    formik.setFieldValue('banner', {
      url: newImage.url,
      seoUrl: '',
      _id: newImage._id as unknown as ID,
    })
  }

  const handleGalleryImageAdded = (newImage: { url: string; _id: string }) => {
    // Add the new gallery image to the existing gallery images
    const newGalleryImages = [...(formik.values.gallery || []), {
      url: newImage.url,
      seoUrl: '',
      _id: newImage._id as unknown as ID,
    }]
    formik.setFieldValue('gallery', newGalleryImages)
  }

  const handleDeleteGalleryImage = async (imageToDelete: images) => {
    const ok = await confirm({ message: intl.formatMessage({ id: 'TOUR.GALLERY.DELETE_CONFIRM' }) })
    if (!ok) return
    try {
      // Use the common request service to delete the image
      const response = await deleteOne(imageToDelete._id, '/images')
      if (response?.payload?.code === 2020) {
        // Filter out the deleted image from gallery
        const updatedGalleryImages = formik.values.gallery?.filter(
          (image) => image._id !== imageToDelete._id
        ) || []
        formik.setFieldValue('gallery', updatedGalleryImages)
        
        toast.success(
          intl.formatMessage({
            id: 'ADMIN.COMMON.SUCCESS.IMAGE_DELETED_SUCCESSFULLY',
          }),
          {
            position: 'top-right',
            autoClose: 4000,
          }
        )
      } else {
        toast.error(
          intl.formatMessage({
            id: 'ADMIN.COMMON.ERROR.IMAGE_DELETED',
          }),
          {
            position: 'top-right',
            autoClose: 4000,
          }
        )
      }
    } catch (error) {
      toast.error(
        intl.formatMessage({
          id: 'ADMIN.COMMON.ERROR.IMAGE_DELETED',
        }),
        {
          position: 'top-right',
          autoClose: 4000,
        }
      )
    }
  }

  // Helper function to add elements
  const handleAddElement = (field: keyof TourDetails, element: any) => {
    const currentValue = formik.values[field] || []
    if (Array.isArray(currentValue)) {
      formik.setFieldValue(field, [...currentValue, element])
    }
  }

  // Form submission handlers
  const handleAddFAQ = () => {
    if (newFAQ.title.trim() && newFAQ.content.trim()) {
      handleAddElement('faqs', { 
        title: newFAQ.title.trim(), 
        content: newFAQ.content.trim() 
      })
      setNewFAQ({ title: '', content: '' })
      setShowFAQForm(false)
    }
  }

  const handleAddInclude = () => {
    if (newInclude.title.trim() && newInclude.description.trim()) {
      handleAddElement('includes', { 
        title: newInclude.title.trim(), 
        description: newInclude.description.trim() 
      })
      setNewInclude({ title: '', description: '' })
      setShowIncludeForm(false)
    }
  }

  const handleAddExclude = () => {
    if (newExclude.title.trim() && newExclude.description.trim()) {
      handleAddElement('excludes', { 
        title: newExclude.title.trim(), 
        description: newExclude.description.trim() 
      })
      setNewExclude({ title: '', description: '' })
      setShowExcludeForm(false)
    }
  }

  const handleAddEducation = () => {
    if (newEducation.name.trim() && newEducation.content.trim() && newEducation.distance.trim()) {
      handleAddElement('education', { 
        name: newEducation.name.trim(),
        content: newEducation.content.trim(),
        distance: newEducation.distance.trim()
      })
      setNewEducation({ name: '', content: '', distance: '' })
      setShowEducationForm(false)
    }
  }

  const handleAddHealth = () => {
    if (newHealth.name.trim() && newHealth.content.trim() && newHealth.distance.trim()) {
      handleAddElement('health', { 
        name: newHealth.name.trim(),
        content: newHealth.content.trim(),
        distance: newHealth.distance.trim()
      })
      setNewHealth({ name: '', content: '', distance: '' })
      setShowHealthForm(false)
    }
  }

  const handleAddTransport = () => {
    if (newTransport.name.trim() && newTransport.content.trim() && newTransport.distance.trim()) {
      handleAddElement('transport', { 
        name: newTransport.name.trim(),
        content: newTransport.content.trim(),
        distance: newTransport.distance.trim()
      })
      setNewTransport({ name: '', content: '', distance: '' })
      setShowTransportForm(false)
    }
  }

  const handlePublishedToggle = () => {
    const newValue = !formik.values.published;
    formik.setFieldValue('published', newValue);
    formik.setFieldValue('status', newValue ? 'published' : 'draft');
  }

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const categoryId = e.target.value
    if (!categoryId) return
    
    const selectedCategory = tourCategories.find((cat: TourCategory) => cat._id === categoryId)
    if (selectedCategory) {
      // Convert existing category to array if it's not already
      // Check if category is already selected
      if (!selectedCategories.some(cat => cat._id === selectedCategory._id)) {
        const newCategory = {
          _id: selectedCategory._id,
          title: selectedCategory.title,
          translations: selectedCategory.translations || [{ title: selectedCategory.title }]
        }
        formik.setFieldValue('category', [...selectedCategories, newCategory])
      }
      setCategoryInput('')
    }
  }

  const handleRemoveCategory = (categoryId: string) => {
    const newCategories = selectedCategories.filter(cat => cat._id !== categoryId)
    formik.setFieldValue('category', newCategories)
  }

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const cityId = e.target.value
    
    if (!cityId) {
      formik.setFieldValue('city', null)
      return
    }

    // Find selected city
    const selectedCity = tourCities.find((city: TourCity) => String(city._id) === String(cityId))
    
    if (selectedCity) {
      // Store only the ID string, not the object
      formik.setFieldValue('city', cityId);
    }
  }

  const selectedRouteCities = React.useMemo(() => {
    const raw = Array.isArray(formik.values.routeCities) ? formik.values.routeCities : []
    return raw
      .map((c: any) => (typeof c === 'string' ? { _id: c } : c))
      .filter((c: any) => isNonEmptyString(c?._id))
  }, [formik.values.routeCities])

  const [routeCityInput, setRouteCityInput] = React.useState('')

  const handleRouteCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const cityId = e.target.value
    if (!cityId) return
    if (selectedRouteCities.some((c: any) => String(c._id) === String(cityId))) {
      setRouteCityInput('')
      return
    }
    formik.setFieldValue('routeCities', [...selectedRouteCities.map((c: any) => c._id), cityId])
    setRouteCityInput('')
  }

  const handleRemoveRouteCity = (cityId: string) => {
    const next = selectedRouteCities.filter((c: any) => String(c._id) !== String(cityId)).map((c: any) => c._id)
    formik.setFieldValue('routeCities', next)
  }

  // Types handlers
  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const typeId = e.target.value
    if (!typeId) return
    const selectedType = tourTypes.find((type: TourType) => type._id === typeId)
    if (selectedType) {
      if (!selectedTypes.some(type => type._id === selectedType._id)) {
        const newType = {
          _id: selectedType._id,
          title: selectedType.title,
          translations: selectedType.translations || [{ title: selectedType.title }]
        }
        formik.setFieldValue('types', [...selectedTypes, newType])
      }
      setTypeInput('')
    }
  }

  const handleRemoveType = (typeId: string) => {
    const newTypes = selectedTypes.filter(type => type._id !== typeId)
    formik.setFieldValue('types', newTypes)
  }

  return (
    <div className='d-flex flex-column gap-7 gap-lg-10'>
      <div className='card card-flush py-4'>
        <div className='card-header'>
          <div className='card-title'>
            <h2>{intl.formatMessage({ id: 'TOUR.FIELDS.GENERAL' })}</h2>
          </div>
          {/* Publish Switch - moved to header and simplified */}
          <div className='card-toolbar'>
            <div className='form-check form-switch form-check-custom form-check-solid me-10'>
              <input
                type='checkbox'
                className='form-check-input w-45px h-30px'
                checked={formik.values.published || false}
                onChange={handlePublishedToggle}
                id="published-switch"
              />
              <label 
                className='form-check-label fw-bold text-gray-700' 
                style={{ width: '80px' }}
                htmlFor="published-switch"
              >
                {formik.values.published 
                  ? intl.formatMessage({ id: 'TOUR.FIELDS.PUBLISHED_STATE_ON' }, { defaultMessage: 'Published' }) 
                  : intl.formatMessage({ id: 'TOUR.FIELDS.PUBLISHED_STATE_OFF' }, { defaultMessage: 'Draft' })}
              </label>
              {/* Add a hidden debug field to show the actual status value */}
              <div className="d-none">
                Status: {formik.values.published ? 'published' : 'draft'}
              </div>
            </div>
          </div>
        </div>
        <div className='card-body pt-0'>
          <div className='mb-10'>
            <label className={`form-label ${isNewTour ? 'required' : ''}`}>
              {intl.formatMessage({ id: 'TOUR.FIELDS.TITLE' })}
            </label>
            <input
              type='text'
              className={`form-control ${
                formik.touched.translations?.[0]?.title && formik.errors.translations 
                  ? 'is-invalid border-danger' 
                  : ''
              }`}
              name='translations[0].title'
              value={formik.values.translations?.[0]?.title || ''}
              onChange={formik.handleChange}
            />
            {formik.touched.translations?.[0]?.title && formik.errors.translations && isNewTour && (
              <div className='invalid-feedback d-block'>
                {intl.formatMessage({ id: 'TOUR.FIELDS.TITLE' })} {intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED' }, { 0: '' })}
              </div>
            )}
          </div>
          <div className='mb-10'>
            <label className='form-label'>
              {intl.formatMessage({ id: 'TOUR.FIELDS.CARD_BADGE' })}
            </label>
            <input
              type='text'
              className='form-control'
              name='translations[0].cardBadge'
              maxLength={80}
              value={formik.values.translations?.[0]?.cardBadge || ''}
              onChange={formik.handleChange}
              placeholder={intl.formatMessage({ id: 'TOUR.FIELDS.CARD_BADGE_PLACEHOLDER' })}
            />
            <div className='form-text'>
              {intl.formatMessage({ id: 'TOUR.FIELDS.CARD_BADGE_HINT' })}
            </div>
            <label className='form-label mt-5'>
              {intl.formatMessage({ id: 'TOUR.FIELDS.CARD_BADGE_COLOR' })}
            </label>
            <div className='d-flex flex-wrap align-items-center gap-3'>
              {CARD_BADGE_COLOR_PRESETS.map((color) => (
                <button
                  key={color}
                  type='button'
                  className='btn p-0 border'
                  style={{
                    width: 28,
                    height: 28,
                    backgroundColor: color,
                    borderRadius: 4,
                    outline:
                      (formik.values.cardBadgeColor || '').toUpperCase() === color
                        ? '2px solid #181c32'
                        : 'none',
                    outlineOffset: 2,
                  }}
                  onClick={() => formik.setFieldValue('cardBadgeColor', color)}
                  aria-label={color}
                />
              ))}
              <input
                type='text'
                className='form-control'
                style={{ maxWidth: 140 }}
                name='cardBadgeColor'
                value={formik.values.cardBadgeColor || ''}
                onChange={formik.handleChange}
                placeholder='#F44D00'
              />
            </div>
          </div>
          <div className='mb-16 w-100' style={{maxWidth: '100%', overflow: 'hidden'}}>
            <label className={`form-label ${isNewTour ? 'required' : ''}`}>
              {intl.formatMessage({ id: 'TOUR.FIELDS.DESCRIPTION' })}
            </label>
            <ReactQuill
              value={formik.values.translations?.[0]?.description || ''}
              onChange={(value) => formik.setFieldValue('translations[0].description', value)}
              onBlur={() => formik.setFieldTouched('translations[0].description', true)}
              modules={{
                toolbar: [
                  ['bold', 'italic', 'underline', 'strike'],
                  ['blockquote'],
                  [{ header: 1 }, { header: 2 }],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  [{ indent: '-1' }, { indent: '+1' }],
                  [{ direction: 'rtl' }],
                  [{ size: ['small', false, 'large', 'huge'] }],
                  [{ header: [1, 2, 3, 4, 5, 6, false] }],
                  [{ font: [] }],
                  ['link'],
                  ['clean'],
                ],
              }}
              formats={[
                'header',
                'font',
                'size',
                'bold',
                'italic',
                'underline',
                'strike',
                'blockquote',
                'list',
                'bullet',
                'indent',
                'link',
                'direction',
              ]}
              className={formik.touched.translations?.[0]?.description && formik.errors.translations 
                ? 'is-invalid' 
                : ''}
              style={{ height: '200px', marginBottom: '100px' }}
            />
            {formik.touched.translations?.[0]?.description && formik.errors.translations && isNewTour && (
              <div className='invalid-feedback d-block'>
                {intl.formatMessage({ id: 'TOUR.FIELDS.DESCRIPTION' })} {intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED' }, { 0: '' })}
              </div>
            )}
          </div>
          {/* Category and Types side by side */}
          <div className='row mb-10 g-5'>
            <div className='col-12 col-md-6'>
              <label className={`form-label ${isNewTour ? 'required' : ''}`}>
                {intl.formatMessage({ id: 'TOUR.FIELDS.CATEGORY' })}
              </label>
              <div className='mb-2'>
                <select
                  className={`form-select ${
                    formik.touched.category && formik.errors.category 
                      ? 'is-invalid border-danger' 
                      : ''
                  }`}
                  value={categoryInput}
                  onChange={handleCategoryChange}
                  disabled={categoriesLoading}
                >
                  <option value=''>{intl.formatMessage({ id: 'COMMON.SELECT' })}</option>
                  {tourCategories.map((category: TourCategory) => {
                    const isSelected = selectedCategories.some(cat => cat._id === category._id)
                    return (
                      <option 
                        key={category._id} 
                        value={category._id}
                        disabled={isSelected}
                      >
                        {resolveCategoryTitle(category, selectedLanguage)}
                      </option>
                    )
                  })}
                </select>
              </div>
              <div className='d-flex flex-wrap gap-2'>
                {selectedCategories.map((category) => {
                  const selectedCategoryTitle =
                    category.translations?.find((t: any) => t.language === selectedLanguage)?.title ||
                    category.translations?.[0]?.title ||
                    ''
                  return (
                    <span
                      key={category._id}
                      style={{
                        background: '#f1f3f7',
                        color: '#333',
                        borderRadius: '999px',
                        padding: '6px 16px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                      }}
                    >
                      {selectedCategoryTitle}
                      <button
                        type='button'
                        className='btn btn-sm btn-link text-danger ms-1 p-0'
                        style={{ lineHeight: 1, fontSize: '1.2rem', marginLeft: 8 }}
                        onClick={() => handleRemoveCategory(category._id)}
                      >
                        &times;
                      </button>
                    </span>
                  )
                })}
              </div>
              {formik.touched.category && formik.errors.category && isNewTour && (
                <div className='invalid-feedback d-block'>
                  {intl.formatMessage({ id: 'TOUR.FIELDS.CATEGORY' })} {intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED' }, { 0: '' })}
                </div>
              )}
            </div>
            <div className='col-12 col-md-6'>
              <label className={`form-label ${isNewTour ? 'required' : ''}`}>
                {intl.formatMessage({ id: 'TOUR.FIELDS.TYPE' })}
              </label>
              <div className='mb-2'>
                <select
                  className={`form-select ${
                    formik.touched.types && formik.errors.types 
                      ? 'is-invalid border-danger' 
                      : ''
                  }`}
                  value={typeInput}
                  onChange={handleTypeChange}
                  disabled={typesLoading}
                >
                  <option value=''>{intl.formatMessage({ id: 'COMMON.SELECT' })}</option>
                  {tourTypes.map((type: TourType) => {
                    const isSelected = selectedTypes.some(t => t._id === type._id)
                    return (
                      <option 
                        key={type._id} 
                        value={type._id}
                        disabled={isSelected}
                      >
                        {resolveTypeTitle(type, selectedLanguage)}
                      </option>
                    )
                  })}
                </select>
              </div>
              <div className='d-flex flex-wrap gap-2'>
                {selectedTypes.map((type) => (
                  <span
                    key={type._id}
                    style={{
                      background: '#f1f3f7',
                      color: '#333',
                      borderRadius: '999px',
                      padding: '6px 16px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                    }}
                  >
                    {type.translations && Array.isArray(type.translations)
                      ? (type.translations.find((t: any) => t.language === selectedLanguage)?.title || type.title)
                      : type.title}
                    <button
                      type='button'
                      className='btn btn-sm btn-link text-danger ms-1 p-0'
                      style={{ lineHeight: 1, fontSize: '1.2rem', marginLeft: 8 }}
                      onClick={() => handleRemoveType(type._id)}
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
              {formik.touched.types && formik.errors.types && isNewTour && (
                <div className='invalid-feedback d-block'>
                  {intl.formatMessage({ id: 'TOUR.FIELDS.TYPE' })} {intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED' }, { 0: '' })}
                </div>
              )}
            </div>
          </div>
          {/* Ville principale + villes du parcours (recherche) — ligne dédiée (évite grille >12 cols invisible selon breakpoints) */}
          <div className='row mb-10'>
            <div className='col-md-6 col-lg-4'>
              <label className={`form-label ${isNewTour ? 'required' : ''}`}>
                {intl.formatMessage({ id: 'TOUR.FIELDS.CITY' })}
              </label>
              <select
                className={`form-select ${
                  formik.touched.city && formik.errors.city 
                    ? 'is-invalid border-danger' 
                    : ''
                }`}
                name='city'
                value={toCitySelectValue(formik.values.city)}
                onChange={handleCityChange}
                disabled={citiesLoading}
              >
                <option value=''>{intl.formatMessage({ id: 'COMMON.SELECT' })}</option>
                {tourCities.map((city: TourCity) => (
                  <option key={city._id} value={city._id}>
                    {city.name} {city.region && city.country ? `(${city.region}, ${city.country})` : city.country ? `(${city.country})` : ''}
                  </option>
                ))}
              </select>
              {formik.touched.city && formik.errors.city && isNewTour && (
                <div className='invalid-feedback d-block'>
                  {intl.formatMessage({ id: 'TOUR.FIELDS.CITY' })} {intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED' }, { 0: '' })}
                </div>
              )}
            </div>
            <div className='col-md-6 col-lg-8'>
              <label className='form-label'>
                {intl.formatMessage({
                  id: 'TOUR.FIELDS.ROUTE_CITIES_SEARCH',
                  defaultMessage: 'Villes du parcours (recherche)',
                })}
              </label>
              <div className='text-muted fs-8 mb-2'>
                {intl.formatMessage({
                  id: 'TOUR.FIELDS.ROUTE_CITIES_SEARCH_HINT',
                  defaultMessage:
                    'Ajoutez ici les villes où le circuit passe (en plus de la ville principale). Les points sur la carte (onglet Localisation) restent un libellé + coordonnées pour le tracé.',
                })}
              </div>
              <div className='d-flex mb-2'>
                <select
                  className='form-select me-2'
                  value={routeCityInput}
                  onChange={handleRouteCityChange}
                  disabled={citiesLoading}
                >
                  <option value=''>{intl.formatMessage({ id: 'COMMON.SELECT' })}</option>
                  {tourCities.map((city: TourCity) => {
                    const isSelected = selectedRouteCities.some((c: any) => String(c._id) === String(city._id))
                    return (
                      <option key={`rc-${city._id}`} value={city._id} disabled={isSelected}>
                        {city.name}
                      </option>
                    )
                  })}
                </select>
              </div>
              <div className='d-flex flex-wrap gap-2'>
                {selectedRouteCities.map((c: any) => {
                  const full = tourCities.find((tc: TourCity) => String(tc._id) === String(c._id))
                  const label = full ? `${full.name}` : String(c._id)
                  return (
                    <span
                      key={`rc-chip-${c._id}`}
                      style={{
                        background: '#f1f3f7',
                        color: '#333',
                        borderRadius: '999px',
                        padding: '6px 16px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                      }}
                    >
                      {label}
                      <button
                        type='button'
                        className='btn btn-sm btn-link text-danger ms-1 p-0'
                        style={{ lineHeight: 1, fontSize: '1.2rem', marginLeft: 8 }}
                        onClick={() => handleRemoveRouteCity(String(c._id))}
                        aria-label='remove'
                      >
                        &times;
                      </button>
                    </span>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Durée + capacité */}
          <div className='row mb-10'>
            <div className='col-md-4'>
              <div className='mb-10 mb-md-0'>
                <label className='form-label'>
                  {intl.formatMessage({ id: 'TOUR.FIELDS.DURATION' })}
                </label>
                <div className="input-group bo-duration-group">
                  <input
                    type='number'
                    className='form-control'
                    placeholder={formik.values.durationUnit === 'days' ? '2' : '24'}
                    name='duration'
                    value={formik.values.duration || ''}
                    onChange={formik.handleChange}
                    min='1'
                    step='1'
                  />
                  <select
                    className='form-select'
                    style={{ maxWidth: '9rem' }}
                    name='durationUnit'
                    value={formik.values.durationUnit || 'hours'}
                    onChange={(e) => {
                      const nextUnit = e.target.value === 'days' ? 'days' : 'hours'
                      const prevUnit = formik.values.durationUnit === 'days' ? 'days' : 'hours'
                      const current = Number(formik.values.duration)
                      if (prevUnit !== nextUnit && Number.isFinite(current) && current > 0) {
                        formik.setFieldValue(
                          'duration',
                          convertDurationDisplayValue(current, prevUnit, nextUnit),
                        )
                      }
                      formik.setFieldValue('durationUnit', nextUnit)
                    }}
                    aria-label={intl.formatMessage({ id: 'TOUR.FIELDS.DURATION_UNIT' })}
                  >
                    <option value='days'>
                      {intl.formatMessage({ id: 'TOUR.PLACEHOLDERS.DURATION.DAYS' })}
                    </option>
                    <option value='hours'>
                      {intl.formatMessage({ id: 'TOUR.PLACEHOLDERS.DURATION.HOURS' })}
                    </option>
                  </select>
                </div>
                <div className='form-text'>
                  {intl.formatMessage({ id: 'TOUR.FIELDS.DURATION_HINT' })}
                </div>
              </div>
            </div>
            <div className='col-md-4'>
              <div className='mb-10 mb-md-0'>
                <label className={`form-label ${isNewTour ? 'required' : ''}`}>
                  {intl.formatMessage({ id: 'TOUR.FIELDS.MIN_PEOPLE' })}
                </label>
                <input
                  type='number'
                  className={`form-control ${
                    formik.touched.tourMinPeople && formik.errors.tourMinPeople 
                      ? 'is-invalid border-danger' 
                      : ''
                  }`}
                  placeholder='2'
                  name='tourMinPeople'
                  value={formik.values.tourMinPeople || ''}
                  onChange={formik.handleChange}
                  min="1"
                />
                {formik.touched.tourMinPeople && formik.errors.tourMinPeople && isNewTour && (
                  <div className='invalid-feedback d-block'>
                    {typeof formik.errors.tourMinPeople === 'string' ? formik.errors.tourMinPeople : intl.formatMessage({ id: 'TOUR.FIELDS.MIN_PEOPLE' }) + ' ' + intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED' }, { 0: '' })}
                  </div>
                )}
              </div>
            </div>
            <div className='col-md-4'>
              <div className='mb-10 mb-md-0'>
                <label className={`form-label ${isNewTour ? 'required' : ''}`}>
                  {intl.formatMessage({ id: 'TOUR.FIELDS.MAX_PEOPLE' })}
                </label>
                <input
                  type='number'
                  className={`form-control ${
                    formik.touched.tourMaxPeople && formik.errors.tourMaxPeople 
                      ? 'is-invalid border-danger' 
                      : ''
                  }`}
                  placeholder='10'
                  name='tourMaxPeople'
                  value={formik.values.tourMaxPeople || ''}
                  onChange={formik.handleChange}
                  min="1"
                />
                {formik.touched.tourMaxPeople && formik.errors.tourMaxPeople && isNewTour && (
                  <div className='invalid-feedback d-block'>
                    {typeof formik.errors.tourMaxPeople === 'string' ? formik.errors.tourMaxPeople : intl.formatMessage({ id: 'TOUR.FIELDS.MAX_PEOPLE' }) + ' ' + intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED' }, { 0: '' })}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className='row g-9 mb-8'>
            {/* Start Date (disponibilite_from) */}
            <div className='col-md-4 fv-row'>
              <label className={`form-label ${isNewTour ? 'required' : ''}`}>
                {intl.formatMessage({ id: 'TOUR.AVAILABILITY.START_DATE' })}
              </label>
              <input
                type='date'
                className={`form-control ${
                  formik.touched.startDate && formik.errors.startDate ? 'is-invalid border-danger' : ''
                }`}
                name='startDate'
                value={formik.values.startDate ? formik.values.startDate.split('T')[0] : ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.startDate && formik.errors.startDate && isNewTour && (
                <div className='invalid-feedback d-block'>
                  {formik.errors.startDate as string}
                </div>
              )}
            </div>
            
            {/* End Date (disponibilite_to) */}
            <div className='col-md-4 fv-row'>
              <label className={`form-label ${isNewTour ? 'required' : ''}`}>
                {intl.formatMessage({ id: 'TOUR.AVAILABILITY.END_DATE' })}
              </label>
              <input
                type='date'
                className={`form-control ${
                  formik.touched.endDate && formik.errors.endDate ? 'is-invalid border-danger' : ''
                }`}
                name='endDate'
                min={isNewTour ? (formik.values.startDate || today) : undefined}
                value={formik.values.endDate ? formik.values.endDate.split('T')[0] : ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.endDate && formik.errors.endDate && isNewTour && (
                <div className='invalid-feedback d-block'>
                  {formik.errors.endDate as string}
                </div>
              )}
            </div>
            
            {/* Booking deadline: number of days before end date (stored as lastBookingDate in API) */}
            <div className='col-md-4 fv-row'>
              <label className='form-label'>
                {intl.formatMessage({ id: 'TOUR.AVAILABILITY.LAST_BOOKING_DATE' })}
              </label>
              <input
                type='number'
                min={0}
                step={1}
                className={`form-control ${
                  formik.touched.bookingDeadlineDays && formik.errors.bookingDeadlineDays
                    ? 'is-invalid border-danger'
                    : ''
                }`}
                name='bookingDeadlineDays'
                placeholder={intl.formatMessage({
                  id: 'TOUR.AVAILABILITY.BOOKING_DEADLINE_DAYS_PLACEHOLDER',
                })}
                value={
                  formik.values.bookingDeadlineDays != null
                    ? formik.values.bookingDeadlineDays
                    : ''
                }
                onChange={(e) => {
                  const raw = e.target.value
                  formik.setFieldValue(
                    'bookingDeadlineDays',
                    raw === '' ? null : parseInt(raw, 10),
                  )
                }}
                onBlur={formik.handleBlur}
              />
              <div className='form-text text-muted'>
                {intl.formatMessage({ id: 'TOUR.AVAILABILITY.BOOKING_DEADLINE_DAYS_HINT' })}
              </div>
              {formik.touched.bookingDeadlineDays && formik.errors.bookingDeadlineDays && (
                <div className='invalid-feedback d-block'>
                  {formik.errors.bookingDeadlineDays as string}
                </div>
              )}
            </div>
          </div>


          <div className='mb-10'>
            <label className={`form-label ${isNewTour ? 'required' : ''}`}>
              {intl.formatMessage({ id: 'TOUR.FIELDS.VIDEO_URL' })}
            </label>
            <input
              type='text'
              className={`form-control ${
                formik.touched.youtubeVideo && formik.errors.youtubeVideo
                  ? 'is-invalid border-danger'
                  : ''
              }`}
              placeholder='https://www.youtube.com/watch?v=example'
              name='youtubeVideo'
              value={formik.values.youtubeVideo || ''}
              onChange={formik.handleChange}
            />
            {formik.touched.youtubeVideo && formik.errors.youtubeVideo && isNewTour && (
              <div className='invalid-feedback d-block'>
                {typeof formik.errors.youtubeVideo === 'string' ? formik.errors.youtubeVideo : intl.formatMessage({ id: 'TOUR.FIELDS.VIDEO_URL' }) + ' ' + intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED' }, { 0: '' })}
              </div>
            )}
            <div className='form-text text-muted'>
              {intl.formatMessage({ id: 'TOUR.FIELDS.VIDEO_URL_HINT' })}
            </div>
          </div>

          {/* Vidéos supplémentaires (multi-formats : YouTube, Vimeo, Reels, TikTok, Facebook, Instagram, MP4 hébergé, etc.) */}
          <div className='form-group mb-3'>
            <label className='me-3 mb-2'>
              <strong>{intl.formatMessage({ id: 'TOUR.FIELDS.VIDEOS' })}</strong>
            </label>
            <div className='form-text text-muted mb-2'>
              {intl.formatMessage({ id: 'TOUR.FIELDS.VIDEOS_HINT' })}
            </div>
            <div className='d-flex flex-column gap-3'>
              {(formik.values.videos || []).map((video: TourVideo, index: number) => (
                <div key={index} className='tour-video-row d-flex flex-row gap-2 align-items-center border rounded p-2'>
                  <select
                    className='form-select form-select-sm w-25'
                    value={video.type}
                    onChange={(e) => {
                      const next = [...(formik.values.videos || [])];
                      next[index] = { ...video, type: e.target.value as TourVideo['type'] };
                      formik.setFieldValue('videos', next);
                    }}
                  >
                    <option value='youtube'>YouTube</option>
                    <option value='vimeo'>Vimeo</option>
                    <option value='dailymotion'>Dailymotion</option>
                    <option value='facebook'>Facebook</option>
                    <option value='tiktok'>TikTok</option>
                    <option value='instagram'>Instagram (Reels)</option>
                    <option value='mp4'>MP4 (hébergé)</option>
                    <option value='url'>Lien direct</option>
                  </select>
                  <input
                    type='text'
                    className='form-control form-control-sm flex-grow-1'
                    placeholder={intl.formatMessage({ id: 'TOUR.FIELDS.VIDEOS_URL_PLACEHOLDER' })}
                    value={video.url}
                    onChange={(e) => {
                      const next = [...(formik.values.videos || [])];
                      next[index] = { ...video, url: e.target.value };
                      formik.setFieldValue('videos', next);
                    }}
                  />
                  <input
                    type='text'
                    className='form-control form-control-sm w-25'
                    placeholder={intl.formatMessage({ id: 'TOUR.FIELDS.VIDEOS_TITLE_PLACEHOLDER' })}
                    value={video.title || ''}
                    onChange={(e) => {
                      const next = [...(formik.values.videos || [])];
                      next[index] = { ...video, title: e.target.value };
                      formik.setFieldValue('videos', next);
                    }}
                  />
                  <button
                    type='button'
                    className='btn btn-icon btn-sm btn-danger'
                    onClick={() => {
                      const next = (formik.values.videos || []).filter((_, i) => i !== index);
                      formik.setFieldValue('videos', next);
                    }}
                    title={intl.formatMessage({ id: 'TOUR.FIELDS.VIDEOS_REMOVE' })}
                  >
                    <i className='bi bi-trash' />
                  </button>
                </div>
              ))}
              <button
                type='button'
                className='btn btn-light btn-sm tour-video-add align-self-start'
                onClick={() => {
                  const next = [...(formik.values.videos || []), { type: 'youtube', url: '', title: '' }];
                  formik.setFieldValue('videos', next);
                }}
              >
                <i className='bi bi-plus-lg me-1' />
                {intl.formatMessage({ id: 'TOUR.FIELDS.VIDEOS_ADD' })}
              </button>
            </div>
          </div>

          {/* Banner Image Section */}
          <div className='form-group mb-3'>
            <label className='me-3 mb-3'>
              <strong>{intl.formatMessage({ id: 'TOUR.BANNER_IMAGE' })}</strong>
            </label>
            <div className='gallery gallery-style'>
              {formik.values.banner && (
                <div className='d-flex flex-row justify-content-space-between'>
                  <img
                    src={formik.values.banner.url.includes('/images/') 
                      ? process.env.REACT_APP_API_URL_IMAGE + formik.values.banner.url 
                      : formik.values.banner.url}
                    alt="Banner"
                    className='border-image h-175px cursor-pointer'
                    onClick={() => formik.values.banner && handleImageClick(formik.values.banner)}
                  />
                  <div className='d-flex flex-column justify-content-space-between'>
                    <span
                      className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                      data-kt-image-input-action='edit'
                      data-bs-toggle='tooltip'
                      title='Edit image'
                    >
                      <i
                        className='cursor-pointer bi bi-pencil-fill fs-7 btn btn-icon btn-circle btn-active-color-primary'
                        onClick={() => formik.values.banner && handleImageClick(formik.values.banner)}
                      ></i>
                    </span>
                  </div>
                </div>
              )}
              {!formik.values.banner && (
                <ImageUploader
                  onImageUpload={(file) => console.log('Image uploaded:', file)}
                  onImageAdded={handleBannerImageAdded}
                  uploadEndpoint='/images'
                  tourId={formik.values.id?.toString()}
                  placement='tour_banner'
                />
              )}
            </div>
          </div>

          {/* Gallery Images Section */}
          <div className='form-group mb-3'>
            <label className='me-3 mb-3'>
              <strong>{intl.formatMessage({ id: 'TOUR.GALLERY_IMAGES' })}</strong>
            </label>
            <div className='gallery gallery-style'>
              {formik.values.gallery?.map((img: images, index: number) => (
                <div key={index} className='d-flex flex-row justify-content-space-between'>
                  <img
                    src={img.url.includes('/images/') 
                      ? process.env.REACT_APP_API_URL_IMAGE + img.url 
                      : img.url}
                    alt={`Gallery ${index}`}
                    className='border-image h-175px cursor-pointer'
                    onClick={() => handleImageClick(img)}
                  />
                  <div className='d-flex flex-column justify-content-space-between'>
                    <span
                      className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                      data-kt-image-input-action='edit'
                      data-bs-toggle='tooltip'
                      title='Edit image'
                    >
                      <i
                        className='cursor-pointer bi bi-pencil-fill fs-7 btn btn-icon btn-circle btn-active-color-primary'
                        onClick={() => handleImageClick(img)}
                      ></i>
                    </span>
                    <span
                      className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                      data-kt-image-input-action='remove'
                      data-bs-toggle='tooltip'
                      title='Remove image'
                    >
                      <i
                        className='cursor-pointer bi bi-x fs-2 btn btn-icon btn-circle btn-active-color-primary'
                        onClick={() => handleDeleteGalleryImage(img)}
                      ></i>
                    </span>
                  </div>
                </div>
              ))}
              <ImageUploader
                onImageUpload={(file) => console.log('Image uploaded:', file)}
                onImageAdded={handleGalleryImageAdded}
                uploadEndpoint='/images'
                tourId={formik.values.id?.toString()}
                placement='tour_gallery'
              />
            </div>
          </div>

          {/* Image Edit Modal */}
          {isModalOpen && selectedImage && (
            <ImageEditModal
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              selectedImage={selectedImage}
              onSave={handleUpdateImage}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export { GeneralTab } 